import type { CreatureDefinition, CreatureFeature } from "../types";

export type SrdCreatureRuleset = "2014" | "2024";

export interface SrdCreatureSearchResult {
  index: string;
  name: string;
  url: string;
}

export interface SrdCreaturePreview {
  index: string;
  name: string;
  ruleset: SrdCreatureRuleset;
  size: string;
  creatureType: string;
  alignment: string;
  armorClass?: number;
  hitPoints?: number;
  hitDice: string;
  speed: Record<string, string | number>;
  abilities: Record<string, number>;
  saves: Record<string, number>;
  skills: Record<string, number>;
  damageVulnerabilities: string[];
  damageResistances: string[];
  damageImmunities: string[];
  conditionImmunities: string[];
  senses: Record<string, string | number>;
  languages: string[];
  challengeRating?: number;
  xp?: number;
  proficiencyBonus?: number;
  traits: CreatureFeature[];
  actions: CreatureFeature[];
  bonusActions: CreatureFeature[];
  reactions: CreatureFeature[];
  legendaryActions: CreatureFeature[];
  spellcasting: Record<string, unknown>;
  sourceUrl: string;
}

const HOST = "https://www.dnd5eapi.co";

async function getJson(url: string) {
  const response = await fetch(url, { headers: { Accept: "application/json" } });

  if (!response.ok) {
    throw new Error(`SRD API returned ${response.status}`);
  }

  return response.json();
}

function featureFromApi(value: any): CreatureFeature {
  return {
    name: String(value?.name || "Feature"),
    description: String(value?.desc || ""),
    attackBonus:
      typeof value?.attack_bonus === "number" ? value.attack_bonus : undefined,
    dc:
      typeof value?.dc?.dc_value === "number"
        ? value.dc.dc_value
        : typeof value?.dc === "number"
          ? value.dc
          : undefined,
    damage: Array.isArray(value?.damage)
      ? value.damage
          .map((entry: any) => entry?.damage_dice)
          .filter(Boolean)
          .join(" + ") || undefined
      : undefined,
    recharge:
      typeof value?.usage?.dice === "string"
        ? value.usage.dice
        : value?.usage?.type === "recharge after rest"
          ? "Rest"
          : undefined,
  };
}

function armorClass(value: unknown): number | undefined {
  if (typeof value === "number") return value;

  if (Array.isArray(value)) {
    const values = value
      .map((entry: any) =>
        typeof entry === "number"
          ? entry
          : typeof entry?.value === "number"
            ? entry.value
            : undefined,
      )
      .filter((entry): entry is number => typeof entry === "number");

    return values.length ? Math.max(...values) : undefined;
  }

  return undefined;
}

function splitLanguages(value: unknown): string[] {
  return String(value || "")
    .split(",")
    .map((language) => language.trim())
    .filter(Boolean);
}

function parseProficiencies(rows: any[]) {
  const saves: Record<string, number> = {};
  const skills: Record<string, number> = {};

  for (const row of rows || []) {
    const name = String(row?.proficiency?.name || "");
    const value = Number(row?.value);

    if (!Number.isFinite(value)) continue;

    if (name.startsWith("Saving Throw: ")) {
      saves[name.replace("Saving Throw: ", "").toLowerCase()] = value;
    }

    if (name.startsWith("Skill: ")) {
      skills[name.replace("Skill: ", "")] = value;
    }
  }

  return { saves, skills };
}

function extractSpellcasting(specialAbilities: any[]) {
  const spellcasting = (specialAbilities || []).find((ability) =>
    String(ability?.name || "")
      .toLowerCase()
      .includes("spellcasting"),
  );

  if (!spellcasting) return {};

  return {
    name: spellcasting.name,
    description: spellcasting.desc || "",
    spellcasting: spellcasting.spellcasting || undefined,
  };
}

export async function searchSrdCreatures(
  query: string,
  ruleset: SrdCreatureRuleset,
): Promise<SrdCreatureSearchResult[]> {
  const data = await getJson(`${HOST}/api/${ruleset}/monsters`);
  const q = query.trim().toLowerCase();
  const rows = Array.isArray(data.results) ? data.results : [];

  return rows
    .filter((row: any) => !q || String(row.name || "").toLowerCase().includes(q))
    .slice(0, 75)
    .map((row: any) => ({
      index: String(row.index),
      name: String(row.name),
      url: row.url ? `${HOST}${row.url}` : `${HOST}/api/${ruleset}/monsters/${row.index}`,
    }));
}

export async function getSrdCreature(
  index: string,
  ruleset: SrdCreatureRuleset,
): Promise<SrdCreaturePreview> {
  const sourceUrl = `${HOST}/api/${ruleset}/monsters/${index}`;
  const data: any = await getJson(sourceUrl);
  const proficiencies = parseProficiencies(data.proficiencies || []);

  return {
    index: String(data.index || index),
    name: String(data.name || index),
    ruleset,
    size: String(data.size || ""),
    creatureType: String(data.type || ""),
    alignment: String(data.alignment || ""),
    armorClass: armorClass(data.armor_class),
    hitPoints:
      typeof data.hit_points === "number" ? data.hit_points : undefined,
    hitDice: String(data.hit_dice || ""),
    speed: data.speed || {},
    abilities: {
      str: Number(data.strength || 0),
      dex: Number(data.dexterity || 0),
      con: Number(data.constitution || 0),
      int: Number(data.intelligence || 0),
      wis: Number(data.wisdom || 0),
      cha: Number(data.charisma || 0),
    },
    saves: proficiencies.saves,
    skills: proficiencies.skills,
    damageVulnerabilities: data.damage_vulnerabilities || [],
    damageResistances: data.damage_resistances || [],
    damageImmunities: data.damage_immunities || [],
    conditionImmunities: (data.condition_immunities || []).map((entry: any) =>
      String(entry?.name || entry),
    ),
    senses: data.senses || {},
    languages: splitLanguages(data.languages),
    challengeRating:
      typeof data.challenge_rating === "number"
        ? data.challenge_rating
        : undefined,
    xp: typeof data.xp === "number" ? data.xp : undefined,
    proficiencyBonus:
      typeof data.proficiency_bonus === "number"
        ? data.proficiency_bonus
        : undefined,
    traits: (data.special_abilities || []).map(featureFromApi),
    actions: (data.actions || []).map(featureFromApi),
    bonusActions: (data.bonus_actions || []).map(featureFromApi),
    reactions: (data.reactions || []).map(featureFromApi),
    legendaryActions: (data.legendary_actions || []).map(featureFromApi),
    spellcasting: extractSpellcasting(data.special_abilities || []),
    sourceUrl,
  };
}

export function creaturePreviewToDefinition(
  preview: SrdCreaturePreview,
  id: string,
  timestamp: string,
): CreatureDefinition {
  return {
    id,
    workspaceId: "local",
    name: preview.name,
    size: preview.size,
    creatureType: preview.creatureType,
    alignment: preview.alignment,
    armorClass: preview.armorClass,
    hitPoints: preview.hitPoints,
    hitDice: preview.hitDice,
    speed: preview.speed,
    abilities: preview.abilities,
    saves: preview.saves,
    skills: preview.skills,
    damageVulnerabilities: preview.damageVulnerabilities,
    damageResistances: preview.damageResistances,
    damageImmunities: preview.damageImmunities,
    conditionImmunities: preview.conditionImmunities,
    senses: preview.senses,
    languages: preview.languages,
    challengeRating: preview.challengeRating,
    xp: preview.xp,
    proficiencyBonus: preview.proficiencyBonus,
    traits: preview.traits,
    actions: preview.actions,
    bonusActions: preview.bonusActions,
    reactions: preview.reactions,
    legendaryActions: preview.legendaryActions,
    mythicActions: [],
    spellcasting: preview.spellcasting,
    lore: "",
    gmNotes: "",
    sourceType: "srd_api",
    sourceRuleset: preview.ruleset,
    sourceExternalId: preview.index,
    sourceUrl: preview.sourceUrl,
    importedAt: timestamp,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}
